import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { dynamicSetup } from '@/mixins/dynamic-setup';
import { recycleDroplet } from '@/mixins/recycle-droplet';
import { queuedWork } from '@/mixins/queued-work';
import { guid } from '@/mixins/guid';
import { siteBuilder } from '@/mixins/site-builder';
import droplets from '@editor/droplets/droplets.config.json';
import vSelect from 'vue-select';
import uploadInput from '../../edit-item/inputs/upload';

export default Vue.component('edit-group', {
  template: require('./template.html'),
  name: 'edit-group',
  mixins: [
    dynamicSetup,
    guid,
    siteBuilder,
    recycleDroplet,
    queuedWork,
  ],
  components: {
    uploadInput,
    vSelect,
  },
  computed: {
    ...mapGetters(['currentSite', 'activeSiteType']),
    options() {
      return this.allCategories;
    },
    typeData() {
      return this.currentSite.dynamic[this.activeSiteType];
    },
    savedPages() {
      return _.map(this.currentSite.pages, 'name');
    },
    groupItems() {
      const list = this.typeData ? _.cloneDeep(this.typeData.published || []) : [];
      const notFound = {
        [this.dynamicData.newAddVal]: 'Unpublished Item',
      };
      return _.map(
        this.activeGroup.items, id =>
          list.find(item => item._id === id) || { ...notFound, _id: this.guid() },
      );
    },
  },
  data() {
    return {
      loading: false,
      strictId: null,
      showAll: false,
      itemLimit: 8,
      paginate: ['items'],
      confirmDeleteGroup: false,
      openCropper: false,
      selectedCategories: [],
      dupeError: false,
      alias: null,
      description: null,
      hitBack: true,
      initialEdits: {
        selectedCategories: [],
        groupItems: [],
        alias: false,
        thumbnail: false,
        description: false,
      },
      newFormDroplets: [],
      siteData: {},
    };
  },
  beforeDestroy() {
    if (this.hitBack) {
      this.onCancel();
    }
  },
  created() {
    this.strictId = this.guid();
    this.setInitialValues();
  },
  props: ['active-group', 'dynamic-data', 'all-categories', 'all-groups'],
  methods: {
    setInitialValues() {
      this.selectedCategories = _.cloneDeep(this.activeGroup.categories);
      this.alias = this.activeGroup.alias;
      this.description = this.activeGroup.description;
      this.initialEdits.selectedCategories = _.cloneDeep(this.activeGroup.categories);
      this.initialEdits.groupItems = _.cloneDeep(this.activeGroup.items);
      this.initialEdits.alias = this.activeGroup.alias;
      this.initialEdits.description = this.activeGroup.description;
      this.initialEdits.thumbnail = this.activeGroup.thumbnail;
    },
    async deleteGroup() {
      this.loading = true;
      const notif = {
        id: this.guid(),
        type: 'success',
        message: 'Group Successfully Deleted!',
      };
      try {
        await this.runPageChecks();
        const groudId = this.activeGroup._id;
        const index = _.findIndex(this.typeData.groups, g => g._id === groudId);
        this.$store.dispatch('spliceDropletVal', {
          array: this.typeData.groups,
          index,
        });
        _.forEach(this.typeData.items, (item) => {
          const index = _.findIndex(item.groups, n => n === this.activeGroup.name);
          if (index > -1) {
            this.$store.dispatch('spliceDropletVal', {
              array: item.groups,
              index,
            });
          }
        });
        _.forEach(this.typeData.published, (item) => {
          const index = _.findIndex(item.groups, n => n === this.activeGroup.name);
          if (index > -1) {
            this.$store.dispatch('spliceDropletVal', {
              array: item.groups,
              index,
            });
          }
        });
        await this.$lightning([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
        // needs to call sitebuilder so that we can remove any searches for groups
        await this.callBuilder({
          publish: 'site',
          site: {
            _id: this.currentSite._id,
          },
        });
        this.hitBack = false;
        this.$emit('reset');
      } catch (e) {
        this.$log.error(e.message || e);
        notif.type = 'error';
        notif.message = `${e.message || e}. This has been reported.`;
        await this._reportError({
          error: e,
          fn: 'deleteGroup',
        });
      } finally {
        this.loading = false;
        this.confirmDeleteGroup = false;
        this.$store.dispatch('addNotification', notif);
      }
    },
    async runPageChecks() {
      // if item has a pageId, delete the page
      if (this.activeGroup.pageId) {
        const page = _.find(this.currentSite.pages, p => p._id === this.activeGroup.pageId);
        const canDelete = this._canDeletePage(page);
        if (page && canDelete) {
          // proceed to delete page
          // remove from sitemap state
          await this.$store.dispatch('removeFromSiteMap', page);
          // go delete the page folder in s3
          const path = this.buildPath(page.url);
          // build path for bucket
          const folder = `${this._customerFolder}/${this._sitePath}${path}`;
          // remove any counted droplets that are on this page
          this.removeCountedDroplets('forms', page);
          // remove page from state
          await this.$store.dispatch('removePage', page);
          const { _id, forms, siteMap } = this.currentSite;
          await this.$lightning([{
            name: 'deletePageItem',
            payload: {
              site: { _id, forms, siteMap },
              item: {
                _id: page._id,
                type: page.type,
              },
              folder,
            },
          }]);
          // if user is deleting the page they're on, set currentPage to homepage
          if (page._id === this.currentPage._id) {
            const homepage = _.find(this.currentSite.pages, {
              isHomepage: true,
            });
            const { item } = await this.checkQueuedWork({ page: homepage });
            this.$store.dispatch('setOriginalPage', item);
            this.$store.dispatch('setCurrentPage', item);
          }
          this.$log.debug('successfully deleted page: ', page.name);
        }
        if (page && !canDelete) {
          throw new Error('This item\'s page has 1 or more sub pages in the sitemap, you\'ll need to remove them before you can delete this. You can do this in the "global site options" window.');
        }
      }
    },
    async saveGroupEdit() {
      this.dupeError = false;
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          const duplicate = _.find(this.allGroups,
            g => g.alias === this.alias && g.name !== this.activeGroup.name);
          if (duplicate) {
            this.dupeError = 'A group with that name already exists.';
            return;
          }
          this.loading = true;
          const notif = {
            id: this.guid(),
            type: 'success',
            message: 'Group Saved!',
          };
          try {
            const functions = [];
            // group item would be the only droplet that would benefit from this like the dynamic
            // item droplets. this new page generation should only run once. if it hasn't been built
            // already
            if (this.typeData.page && !this.typeData.page.modal && this.typeData.page.groupPageId) {
              const pageCreated = _.find(
                this.currentSite.pages, p => p._id === this.activeGroup.pageId);
              const groupPage = _.find(
                this.typeData.pages || [], p => p._id === this.typeData.page.groupPageId,
              );
              if (!pageCreated && groupPage) {
                // we need to do a name check because if there is another page with the same
                // name that already exists, this will cause problems in s3 as there can't be
                // 2 folders with the same name
                const newPage = await this.generateFromDynamic(groupPage);
                if (!this.savedPages.includes(newPage.name)) {
                  const limits = this._checkLimits(this.siteData);
                  if (!limits.pages.reached && !limits.forms.reached) {
                    this.$store.dispatch('addPage', newPage);
                    this.$store.dispatch('addToSiteMap', newPage);
                    functions.push({
                      name: 'addPageItem',
                      payload: {
                        siteId: this.currentSite._id,
                        siteMap: this.currentSite.siteMap,
                        item: newPage,
                      },
                    });
                    // link new page id to item
                    this.$store.dispatch('setDropletVal', {
                      drp: this.activeGroup,
                      key: 'pageId',
                      val: newPage._id,
                    });
                    // also add pageLink to droplet
                    this.$store.dispatch('setDropletVal', {
                      drp: this.activeGroup,
                      key: 'pageLink',
                      val: newPage._id,
                    });
                    // add actual limit counts to overall site totals
                    _.forEach(this.newFormDroplets, (drp) => {
                      this.$store.dispatch('addFormCount', { drp });
                    });
                  }
                  // if user has reached certain limits based on tier, don't create new page but
                  // also don't stop the publishing of this item
                  if (limits.pages.reached) {
                    this.$store.dispatch('addNotification', {
                      id: this.guid(),
                      type: 'warning',
                      message: 'This site has reached the page limit for this plan, therefore no new page was generated for this group.',
                    });
                  }
                  if (limits.forms.reached) {
                    this.$store.dispatch('addNotification', {
                      id: this.guid(),
                      type: 'warning',
                      message: 'This site has reached the form limit and the dynamic page tied to these groups contains 1 or more forms, therefore no new page was generated for this group.',
                    });
                  }
                } else {
                  this.$store.dispatch('addNotification', {
                    id: this.guid(),
                    type: 'warning',
                    message: `There is already a page with the name '${newPage.name}' so no new page was generated for this group. If you want to generate a page, first delete the other page and then come back to the group to re-publish.`,
                  });
                }
              }
              // if published already, we need to update page meta information
              if (pageCreated) {
                const title = this.activeGroup.alias;
                this.$store.dispatch('setGlobalValue', {
                  item: pageCreated,
                  key: 'name',
                  value: title,
                });
                this.$store.dispatch('setGlobalValue', {
                  item: pageCreated,
                  key: 'title',
                  value: `${this.currentSite.alias} | ${title}`,
                });
                let desc = this.activeGroup.description || this.activeGroup.alias;
                if (desc) {
                  desc = this.truncate(desc, 160);
                }
                this.$store.dispatch('setGlobalValue', {
                  item: pageCreated,
                  key: 'description',
                  value: desc,
                });
                const metaImage = this.activeGroup.thumbnail;
                this.$store.dispatch('setGlobalValue', {
                  item: pageCreated.meta,
                  key: 'image',
                  value: metaImage,
                });

                // save to dynamo
                functions.push({
                  name: 'updatePageAttr',
                  payload: {
                    siteId: this.currentSite._id,
                    pageId: pageCreated._id,
                    updates: [
                      { key: 'name', value: title },
                      { key: 'title', value: `${this.currentSite.alias} | ${title}` },
                      { key: 'description', value: desc },
                      { key: 'meta', value: pageCreated.meta },
                    ],
                  },
                });
              }
            }

            functions.push({
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'dynamic',
                value: this.currentSite.dynamic,
              },
            });

            await this.$lightning(functions);

            await this.callBuilder({
              publish: 'site',
              site: {
                _id: this.currentSite._id,
              },
            });
            this.hitBack = false;
            this.$emit('reset');
          } catch (e) {
            this.$log.error(e.message || e);
            notif.type = 'error';
            notif.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'saveGroupEdit',
            });
          } finally {
            this.loading = false;
            this.confirmDeleteGroup = false;
            this.$store.dispatch('addNotification', notif);
          }
        }
      });
    },
    async generateFromDynamic(groupPage) {
      const groupItem = this.activeGroup;
      const page = _.cloneDeep(groupPage);
      page._id = this.guid();
      page.createdDate = new Date().toISOString();
      page.type = 'page';
      page.groupItemId = groupItem._id;
      // auto set meta information
      if (groupItem.alias) {
        page.defaultTitle = false;
        page.title = `${this.currentSite.alias} | ${groupItem.alias}`;
      }
      if (groupItem.description) {
        page.description = this.truncate(
          groupItem.description || groupItem.alias,
          160,
        );
      }
      if (groupItem.thumbnail) {
        page.defaultMeta = false;
        page.meta.image = groupItem.thumbnail;
      }
      const title = groupItem.alias || `Untitled-${Date.now()}`;
      page.url = `${this.makeURLStr(title)}/`;
      page.name = title;
      // setup site data to check limits
      this.siteData.tier = this.currentSite.tier;
      this.siteData.pages = _.clone(this.currentSite.pages);
      this.siteData.pages.push(page);
      this.siteData.drafts = _.clone(this.currentSite.drafts);
      this.siteData.templates = _.clone(this.currentSite.templates);
      this.siteData.forms = _.clone(this.currentSite.forms);
      this.siteData.recycled = _.clone(this.currentSite.recycled);
      this.newFormDroplets = [];
      page.blocks = await Promise.all(page.blocks.map(droplet => new Promise(async (res) => {
        if (droplet.templateRegion) {
          res(droplet);
        } else {
          const newId = this.guid();
          this.updateStyleString({
            oldId: droplet._id,
            newId,
            droplet,
          });
          droplet._id = newId;
          await this.routeRecycleAction({
            action: 'saveNewRecycled',
            newId,
            type: this._dropletType(droplet),
            droplet,
          });
          // we need to find droplets in each block that need to link to this dynamic data
          // they should in 99.9% of the time.
          await this._deepFind(
            {
              entry: [droplet],
              key: 'type',
            }, async (item) => {
              if (item._id) {
                const type = this._dropletType(item);
                // check if droplet found should be set to active dynamic item
                if (type.any && type.name) {
                  const config = droplets[type.name];
                  const link = config.linkDynamicItem;
                  if (link && (link.type === this.activeSiteType || link.type === 'all')) {
                    item[link.key] = groupItem._id;
                    item.siteType = this.activeSiteType;
                  }
                }
                if (type.any && type.name === 'form') {
                  this.siteData.forms.push(item._id);
                  this.newFormDroplets.push(item);
                }
              }
            },
          );
          res(droplet);
        }
      })));
      return page;
    },
    truncate(text, limit) {
      const description = text || '';
      if (description.length < limit) {
        return description;
      }
      return `${description.substring(0, limit)}...`;
    },
    makeURLStr(str = '') {
      const url = str.replace(/[^a-zA-Z0-9]/g, '-');
      return _.chain(url)
        .toLower()
        .replace(/ /g, '-')
        .value();
    },
    updateCategories(val) {
      this.$store.dispatch('setDropletVal', {
        drp: this.activeGroup,
        key: 'categories',
        val: _.cloneDeep(val),
      });
    },
    onNameInput() {
      this._debounce(() => {
        this.$store.dispatch('setDropletVal', {
          drp: this.activeGroup,
          key: 'alias',
          val: this._getInputVal(this.alias),
        });
      }, 250);
    },
    onDescInput() {
      this._debounce(() => {
        this.$store.dispatch('setDropletVal', {
          drp: this.activeGroup,
          key: 'description',
          val: this._getInputVal(this.description),
        });
      }, 250);
    },
    onCancel() {
      this.$store.dispatch('setDropletVal', {
        drp: this.activeGroup,
        key: 'categories',
        val: _.cloneDeep(this.initialEdits.selectedCategories),
      });
      this.$store.dispatch('setDropletVal', {
        drp: this.activeGroup,
        key: 'items',
        val: _.cloneDeep(this.initialEdits.groupItems),
      });
      this.$store.dispatch('setDropletVal', {
        drp: this.activeGroup,
        key: 'alias',
        val: this.initialEdits.alias,
      });
      this.$store.dispatch('setDropletVal', {
        drp: this.activeGroup,
        key: 'description',
        val: this.initialEdits.description,
      });
      this.$store.dispatch('setDropletVal', {
        drp: this.activeGroup,
        key: 'thumbnail',
        val: this.initialEdits.thumbnail,
      });
      this.hitBack = false;
      this.$emit('reset');
    },
    removeCountedDroplets(list, item) {
      if (item[list] && this.currentSite[list]) {
        _.forEach(item[list], (_id) => {
          const index = _.findIndex(this.currentSite[list], f => f === _id);
          if (index >= 0) {
            this.$store.dispatch('spliceDropletVal', {
              array: this.currentSite[list],
              index,
            });
          }
        });
      }
    },
    getThumbnail(item) {
      const image = item.image || item.thumbnail || '';
      return `url('${this._servePrefix(image) + image}')`;
    },
    setCropper(val) {
      this.openCropper = val;
    },
    toLower(word) {
      return _.lowerCase(word);
    },
    buildPath(url) {
      // Since the sitemap makes links take a depth, we need to make sure
      // it is properly doing so.
      let path = url;
      if (!path.endsWith('/') && path !== '/') {
        // remove last "/" if there
        path = `${path}/`;
      }
      if (!path.startsWith('/') && path !== '/') {
        // remove the first "/" if there
        path = `/${path}`;
      }
      if (path.trim() === '') {
        path = '/';
      }
      return path;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/groups/edit-group/index.js